// export default Audit;
import React, { useState, useEffect, useCallback } from "react";
import { Route, Routes, useLocation, useMatch } from "react-router-dom";
import { Helmet } from "react-helmet";
import Filter from "../../elements/filter";
import ReportSummary from "../../elements/reportSummary";
import AuditSummary from "./views/auditSummary";
import DeletionSummary from "./views/deletionSummary";
import VoidSummary from "./views/voidSummary";
import DetailedDiscountSummary from "./views/detailedDiscountSummary";
import "../style.scss";
import { useAppContext } from "../../../../AppContext";

function Audit() {
  const location = useLocation();
  const match = useMatch("/app/audit/*");
  const {
    user,
    chimeraserv,
    components,
    filterValue,
    setFilterValue,
    preservedAuditData,
    setPreservedAuditData,
  } = useAppContext();
  const props = { user, chimeraserv, components };

  const [filter, setFilter] = useState(null);
  const [page, setPage] = useState(location.pathname);
  const [isPreservedSession, setIsPreservedSession] = useState(false);

  const routes = [
    {
      name: "Audit Summary",
      path: "logs",
      component: AuditSummary,
    },
    {
      name: "Void Summary",
      path: "voids",
      component: VoidSummary,
    },
    {
      name: "Deletion Summary",
      path: "deletion",
      component: DeletionSummary,
    },
    {
      name: "Detailed Discount Summary",
      path: "discount",
      component: DetailedDiscountSummary,
    },
  ];

  // Check if we should preserve data
  const shouldPreserveData =
    location.state?.prevUrl?.includes("/app/view") && preservedAuditData;

  // Load preserved data on mount if available
  useEffect(() => {
    // If we have preservedAuditData but no filter, and coming from view
    if (
      preservedAuditData &&
      !filter &&
      location.state?.prevUrl?.includes("/app/view")
    ) {
      setFilter(preservedAuditData.filter);
      setFilterValue(preservedAuditData.filter);
      setIsPreservedSession(true);
      // Clear the state after using it
      window.history.replaceState({}, "", location.pathname);
    }
  }, [preservedAuditData, filter, filterValue, location.state]);

  // Handle location changes
  useEffect(() => {
    if (location.pathname !== page) {
      if (shouldPreserveData) {
        // Restore preserved data

        setFilter(preservedAuditData.filter);
        setFilterValue(preservedAuditData.filter);

        setIsPreservedSession(true);
        // Clear the state after using it
        window.history.replaceState({}, "", location.pathname);
      } else if (!location.state?.prevUrl?.includes("/app/view")) {
        // Clear filter for normal navigation (not from view)

        setFilter(null);
        setFilterValue(null);
        setIsPreservedSession(false);
        // Only clear preserved data if not coming from view
        setPreservedAuditData(null);
      }

      setPage(location.pathname);
    }
  }, [
    location.pathname,
    location.state,
    page,
    shouldPreserveData,
    preservedAuditData,
    setPreservedAuditData,
  ]);

  // Set filter and save to context IMMEDIATELY
  const handleSetFilter = useCallback(
    (filters) => {
      // Save to context IMMEDIATELY (before clearing current filter)
      setPreservedAuditData({
        filter: filters,
        timestamp: Date.now(),
        path: location.pathname,
      });

      // Clear current filter first
      setFilter(null);
      setFilterValue(null);
      setIsPreservedSession(false);

      setTimeout(() => {
        setFilter(filters);
        setFilterValue(filters);
        setPage(location.pathname);
      }, 300);
    },
    [location.pathname, setPreservedAuditData]
  );

  // Clear filter function
  const clearFilter = useCallback(() => {
    setFilter(null);
    setFilterValue(null);
    setPreservedAuditData(null);
    setIsPreservedSession(false);
  }, [setPreservedAuditData]);

  // Title logic
  const getTitle = () => {
    let titleObj = routes.find(
      (route) => route.path === location.pathname.split("/")[3]
    );
    if (location.pathname.split("/").length === 3) {
      titleObj = { name: "Audit Summary" };
    }
    return titleObj;
  };

  const renderTitle = (title) => {
    let name = "Audit";
    if (title) {
      name = title.name + " | Audit";
      if (location.pathname.split("/").length === 3) {
        name = "Audit Summary";
      }
    }
    return (
      <Helmet>
        <title>{`${name} | Chimera Solutions | Insights`}</title>
      </Helmet>
    );
  };

  // Check if we're in a preserved session or should preserve
  const showPreservationBanner =
    shouldPreserveData ||
    isPreservedSession ||
    (location.state?.prevUrl?.includes("/app/view") && preservedAuditData);

  return (
    <section>
      {renderTitle(getTitle())}
      <Filter
        location={location.pathname}
        setFilter={handleSetFilter}
        key={location.pathname}
      />

      <div className={["report"]}>
        <div className={["viewTitle"]}>
          {getTitle() ? getTitle().name : <div />}
        </div>

        <Routes>
          <Route
            path="/"
            element={
              filter ? (
                <section>
                  <ReportSummary filter={filter} />
                  <AuditSummary
                    user={props.user}
                    chimeraserv={props.chimeraserv}
                    filter={filter}
                    preserveData={isPreservedSession || showPreservationBanner}
                  />
                </section>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    margin: "20px 0",
                  }}
                >
                  <h4>No Report Generated</h4>
                  <p>
                    Select filters and click "Generate Report" to view audit
                    data.
                  </p>
                </div>
              )
            }
          />

          {routes.map((route, key) => (
            <Route
              key={key}
              path={route.path}
              element={
                filter ? (
                  <section>
                    <ReportSummary filter={filter} />
                    <route.component
                      user={props.user}
                      chimeraserv={props.chimeraserv}
                      filter={filter}
                      preserveData={
                        isPreservedSession || showPreservationBanner
                      }
                      key={`${route.path}-${filter?.timestamp || "default"}`}
                    />
                  </section>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      margin: "20px 0",
                    }}
                  >
                    <h4>No Report Generated</h4>
                    <p>
                      Select filters and click "Generate Report" to view{" "}
                      {route.name.toLowerCase()}.
                    </p>
                  </div>
                )
              }
            />
          ))}

          <Route
            path="*"
            element={
              props.components.setting[0].component
                ? React.createElement(props.components.setting[0].component)
                : null
            }
          />
        </Routes>
      </div>
    </section>
  );
}

export default Audit;
